import React from 'react';
import { Link } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { loginDefaultValues, loginSchema } from './assets/authUtil';
import { useDispatch } from 'react-redux';
import {
  GOOGLE_AUTH_URL,
  setCredentials,
  setUserBasicInfo,
  useLoginMutation,
} from './authSlice';
import FormikInputField from '../../app/components/formikComponents/FormikInputField';
import StyledButton from '../../app/components/styledComponents/StyledButton';
import { toast } from 'react-toastify';
import { Col, Container, Row } from 'react-bootstrap';
import StyledText from '../../app/components/styledComponents/StyledText';
import GoogleSvgIcon from '../../app/components/svgIcons/GoogleSvgIcon';
import styled from 'styled-components';
import LoginImage from './assets/images/LoginImage.png';
import BoostFloralAvatarIcon from '../../app/components/svgIcons/BoostFloralAvatarIcon';
import { setTheme } from '../../app/services/themeSlice';
import { getTheme } from '../../utils/helper';
import { colorPaletteOptions } from '../settings/assets/settingsUtils';
import { Helmet } from 'react-helmet';
import SeoHead from '../../app/components/SeoHead';

const Login = () => {
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();

  const seoData = {
    title: 'Login | BoostFloral',
    description:
      'Building and Hosting your e-commerce Floral Website has never been easier or quicker.  The BoostFloral platform addresses the specific needs of Florists.',
    keywords:
      'Flower Shop Websites, Floral Web Design Software, Floral CMS, Building a Floral Website, Floral POS, Floral point of sale, Floral website builder, Build a Floral Website, Florist website design, Floral website design, best floral website templates, Florist web templates, Create a flower shop website, Floral e-commerce website, Best website platform for florists, Flower shop online store, Florist online ordering system, Flower delivery website, POS system for florists, Florist website SEO, Customizable florist website, Floral event booking website, the best website for retail flower shops',
    ogTitle: 'Login | BoostFloral',
    ogDescription:
    'Building and Hosting your e-commerce Floral Website has never been easier or quicker.  The BoostFloral platform addresses the specific needs of Florists.',
    ogType: 'company',
    ogSiteName: 'BoostFloral',
    ogImage: 'https://www.boostfloral.com/logo.svg',
  };

  return (
    <div className="px-3 px-md-5 pb-3 d-flex align-items-center flex-grow-1">
      <SeoHead data={seoData}/>
      <Container className="auth-inner bg-white rounded-3 mt-3 p-3 p-md-3 px-md-4 pb-md-3">
        <Row>
          <Col xs={12}>
            <h2 className="mb-2 mb-md-4">Login</h2>
          </Col>
          <StyledLeftImage xs={12} md={5}>
            <div className="d-flex flex-column gap-3 justify-content-between align-items-center my-3">
              <BoostFloralAvatarIcon />
              <StyledText
                as="h1"
                color="secondary"
                className="fw-bolder text-uppercase"
              >
                Welcome back!
              </StyledText>
            </div>
          </StyledLeftImage>

          <Col xs={12} md={7}>
            <Formik
              initialValues={{
                ...loginDefaultValues,
              }}
              validationSchema={loginSchema}
              onSubmit={async (values) => {
                const { data } = await login({ ...values });

                if (data?.accessToken) {
                  dispatch(
                    setUserBasicInfo({
                      values:
                        {
                          firstName: data?.user?.firstName || '',
                          lastName: data?.user?.lastName || '',
                          imageUrl: data?.user?.imageUrl || '',
                        } || {},
                    }),
                  );
                  dispatch(setCredentials({ values: data }));
                  const themeSettings = data.user?.themeSettings;
                  const { customColors, colorPalette } = themeSettings
                    ? JSON.parse(themeSettings)
                    : {
                        customColors: colorPaletteOptions[0].primaryColors,
                        colorPalette: 'default',
                      };
                  const option =
                    colorPaletteOptions.find(
                      ({ value }) => value === colorPalette,
                    ) || colorPaletteOptions[0];

                  dispatch(setTheme({ theme: getTheme(option, customColors) }));
                  toast.success('Login successfully!');
                }
              }}
            >
              {({ handleSubmit }) => (
                <Form onSubmit={handleSubmit}>
                  <div className="mb-2 mt-3 mt-md-0">
                    <StyledText
                      as="label"
                      color="secondary"
                      htmlFor={`email`}
                      className="form-label"
                    >
                      Email *
                    </StyledText>
                    <FormikInputField
                      name={`email`}
                      id={`email`}
                      placeholder="Type in"
                    />
                  </div>

                  <div className="mb-2">
                    <StyledText
                      as="label"
                      color="secondary"
                      htmlFor={`password`}
                      className="form-label"
                    >
                      Password *
                    </StyledText>
                    <FormikInputField
                      type="password"
                      name={`password`}
                      id={`password`}
                      placeholder="Type in"
                    />
                  </div>

                  <div className="d-flex justify-content-center my-4">
                    <Link
                      to="/reset-password"
                      className="w-100 fw-bolder text-center text-decoration-none"
                    >
                      Forgot Password
                    </Link>
                  </div>

                  <StyledButton
                    type="submit"
                    className="w-100 my-2 py-2 fw-bolder p-1"
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    Login
                  </StyledButton>

                  <StyledCircleText className="rounded-circle mx-auto my-3">
                    <h5 className="mb-0">OR</h5>
                  </StyledCircleText>

                  <a className="text-decoration-none" href={GOOGLE_AUTH_URL}>
                    <StyledButton
                      className="w-100  my-2 py-2"
                      variant="outline"
                      type="button"
                    >
                      <div className="d-flex align-items-center gap-3">
                        <div className="d-flex">
                          <GoogleSvgIcon />
                        </div>
                        <span className="fw-bolder">Log using Google</span>
                      </div>
                    </StyledButton>
                  </a>

                  <div className="d-flex justify-content-center flex-wrap mt-2 fw-bolder ">
                    <StyledText color="darkenText">New User?</StyledText>
                    <StyledText
                      as={Link}
                      className="ms-1 text-decoration-none"
                      to="/signup"
                      color="primary"
                    >
                      SIGN UP
                    </StyledText>
                  </div>
                </Form>
              )}
            </Formik>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const StyledLeftImage = styled(Col)`
  background-image: url(${LoginImage});
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top center;
  border-radius: 10px;
`;

const StyledCircleText = styled.div`
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.secondary};

  h5 {
    color: ${({ theme }) => theme.white};
  }
`;
export default Login;
